import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {createSelector} from 'reselect';
import {v5 as uuidv5} from 'uuid';

import type {RootState} from '../../types';
import {
	ICustomerStateStore,
	CustomerGroup,
	LoyaltyProfile,
	LoyaltyActivity,
	ABTestParticipations,
	ABTestsResponse,
} from './types';
import {ICustomerQualifiers, INewsletterPreferences} from 'coreClasses/customer/ICustomer';

const customerSelector = (state: RootState) => state.customer;
const contextSelector = (state: RootState) => state.customer.context;
const loyaltyProfileSelector = (state: RootState) => state.customer.loyaltyProfile;
const loyaltyActivitySelector = (state: RootState) => state.customer.loyaltyActivity;
const customerGroupsSelector = (state: RootState) => state.customer.customerGroups;
const customerPersonalizationKeySelector = (state: RootState) => state.customer.customerPersonalizationKey;
const customerGroupsKeySelector = (state: RootState) => state.customer.customerGroupsKey;
const abTestParticipationsSelector = (state: RootState) => state.customer.ABTestParticipations;

const initialState = {
	isFirstAuthComplete: false,
	loyaltyProfile: {
		isLoading: false,
		loaded: false,
	},
	loyaltyActivity: {
		isLoading: false,
		loaded: false,
	},
} as ICustomerStateStore;

export const customerSlice = createSlice({
	name: 'customer',
	initialState: initialState,
	reducers: {
		resetToInitialState: () => {
			return initialState;
		},
		receiveCustomer: (state: ICustomerStateStore, action: PayloadAction<Partial<ICustomerStateStore>>) => {
			let isFirstAuthComplete: boolean = state.isFirstAuthComplete;

			if (action.payload.authType && !state.isFirstAuthComplete) {
				isFirstAuthComplete = true;
			} else if (!action.payload.authType && state.isFirstAuthComplete) {
				isFirstAuthComplete = false;
			}

			Object.assign(state, action.payload, {isFirstAuthComplete});
		},
		receiveContext: (state: ICustomerStateStore, action: PayloadAction<ICustomerQualifiers | null>) => {
			Object.assign(state, {context: action.payload});
		},
		receiveCustomerGroups: (state: ICustomerStateStore, action: PayloadAction<CustomerGroup[]>) => {
			const customerGroups = action.payload.sort((a, b) => b.priority - a.priority);
			const refinementCustomerGroups = customerGroups.filter((group: CustomerGroup) => group.isRefinement);
			const customerPersonalizationKey = refinementCustomerGroups
				.map((customerGroup: CustomerGroup) => {
					return customerGroup.ID;
				})
				.join('|');
			const customerGroupIDs = customerGroups?.map((customerGroup: CustomerGroup) => customerGroup.ID) || [];
			const customerGroupsKey = customerGroupIDs.length > 0 ? uuidv5(customerGroupIDs.join(','), uuidv5.URL) : null;

			Object.assign(state, {customerGroups, customerGroupsKey, customerPersonalizationKey});
		},
		receiveABTestParticipations: (state: ICustomerStateStore, action: PayloadAction<ABTestsResponse>) => {
			const ABTestParticipations: ABTestParticipations = {};

			action.payload.forEach((participation) => {
				const testId = participation.test;
				ABTestParticipations[testId] = participation.segment;
			});

			Object.assign(state, {ABTestParticipations});
		},
		receiveLoyaltyProfile: (
			state: ICustomerStateStore,
			action: PayloadAction<Partial<Omit<LoyaltyProfile, 'isLoading' | 'loaded'>>>,
		) => {
			Object.assign(state, {
				loyaltyProfile: {
					isLoading: false,
					loaded: true,
					...action.payload,
				},
			});
		},
		receiveLoyaltyActivity: (
			state: ICustomerStateStore,
			action: PayloadAction<Partial<Omit<LoyaltyActivity, 'isLoading' | 'loaded'>>>,
		) => {
			Object.assign(state, {
				loyaltyActivity: {
					isLoading: false,
					loaded: true,
					...action.payload,
				},
			});
		},
		receiveHasOrders: (state: ICustomerStateStore, action: PayloadAction<boolean>) => {
			Object.assign(state, {
				hasOrders: action.payload,
			});
		},
		receiveNewsletterSubscribe: (state: ICustomerStateStore, action: PayloadAction<boolean>) => {
			Object.assign(state, {
				newsletter: action.payload,
			});
		},
		receiveNewsletterCustomization: (state: ICustomerStateStore, action: PayloadAction<INewsletterPreferences>) => {
			if (action.payload.gender) {
				action.payload.gender = Number(action.payload.gender);
			}

			Object.assign(state, {
				newsletterCustomization: action.payload,
			});
		},
	},
});

export const {
	receiveCustomer,
	receiveContext,
	receiveCustomerGroups,
	receiveABTestParticipations,
	receiveLoyaltyProfile,
	receiveLoyaltyActivity,
	receiveCustomerInstanceId,
	receiveHasOrders,
	receiveNewsletterSubscribe,
	receiveNewsletterCustomization,
	resetToInitialState,
} = customerSlice.actions as any;

export const selectCustomer = createSelector(customerSelector, (customer) => customer);
export const selectContext = createSelector(contextSelector, (context) => context);
export const selectLoyaltyProfile = createSelector(loyaltyProfileSelector, (loyaltyProfile) => loyaltyProfile);
export const selectLoyaltyActivity = createSelector(loyaltyActivitySelector, (loyaltyActivity) => loyaltyActivity);
export const selectCustomerGroups = createSelector(customerGroupsSelector, (customerGroups) => customerGroups);
export const selectCustomerPersonalizationKey = createSelector(
	customerPersonalizationKeySelector,
	(customerPersonalizationKey) => customerPersonalizationKey,
);
export const selectCustomerGroupsKey = createSelector(
	customerGroupsKeySelector,
	(customerGroupsKey) => customerGroupsKey,
);
export const selectABTestParticipations = createSelector(
	abTestParticipationsSelector,
	(abTestParticipations) => abTestParticipations,
);

export default customerSlice.reducer;
